import React, { useMemo } from 'react';

function Pill({ children }) {
  return (
    <span
      style={{
        background: 'var(--badge-pill-bg, rgba(148,163,184,.15))',
        color: 'var(--badge-pill-fg, inherit)',
        padding: '6px 10px 5px',
        borderRadius: 10,
        fontWeight: 500,
        letterSpacing: '.35px',
        lineHeight: 1.1,
        fontSize: 12,
      }}
    >
      {children}
    </span>
  );
}

const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

const fmtPct = (n, dp = 1) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  return `${n.toFixed(dp)}%`;
};

/**
 * BadgeStatsBlock
 * Props:
 *  - badge: object from usePlayerBadges (can include rarity* / globalEarnCount / progress* / timestamps)
 *  - meta: optional meta from hook (if you expose total player base, etc.)
 *  - colors(optional): to allow you to pass BadgeGallery colors; if omitted, uses neutral chips
 */
export default function BadgeStatsBlock({ badge, meta, colors }) {
  const progress = Number(
    badge?.progressPercent ?? badge?.progress?.percent ?? 0,
  );

  const pills = useMemo(() => {
    const xs = [];

    if (badge?.rarityLabel)
      xs.push(<Pill key="rarityLabel">Rarity: {badge.rarityLabel}</Pill>);

    if (typeof badge?.rarityScore === 'number')
      xs.push(<Pill key="rarityScore">Rarity Score: {badge.rarityScore}</Pill>);

    if (typeof badge?.globalEarnCount === 'number') {
      xs.push(
        <Pill key="globalEarnCount">
          Global Earned: {fmtInt(badge.globalEarnCount)}
        </Pill>,
      );
    }

    if (progress > 0 && !badge?.earnedAt) {
      xs.push(<Pill key="progress">Progress: {fmtInt(progress)}%</Pill>);
    }

    if (badge?.threshold != null && badge?.metric) {
      xs.push(
        <Pill key="threshold">
          Threshold: {fmtInt(badge.threshold)} {badge.metric}
        </Pill>,
      );
    }

    if (badge?.type) xs.push(<Pill key="type">Type: {badge.type}</Pill>);
    if (badge?.category)
      xs.push(<Pill key="category">Category: {badge.category}</Pill>);
    if (badge?.season)
      xs.push(<Pill key="season">Season: {badge.season}</Pill>);

    if (badge?.firstEarnedAt) {
      xs.push(
        <Pill key="firstEarnedAt">
          First Earned: {new Date(badge.firstEarnedAt).toLocaleDateString()}
        </Pill>,
      );
    }

    if (badge?.lastEarnedAt) {
      xs.push(
        <Pill key="lastEarnedAt">
          Last Earned: {new Date(badge.lastEarnedAt).toLocaleDateString()}
        </Pill>,
      );
    }

    if (badge?.earnedAt) {
      xs.push(
        <Pill key="earnedAt">
          You Earned: {new Date(badge.earnedAt).toLocaleString()}
        </Pill>,
      );
    }

    // If total player base exists in meta, compute an approximate rarity percentile
    const totalPlayers = meta?.totalPlayers ?? meta?.playerBase;
    if (
      typeof totalPlayers === 'number' &&
      totalPlayers > 0 &&
      typeof badge?.globalEarnCount === 'number'
    ) {
      const pct = (badge.globalEarnCount / totalPlayers) * 100;
      xs.push(
        <Pill key="rarityPercentile">
          Owned by ~{fmtPct(pct ?? 0, 2)} of players
        </Pill>,
      );
    }

    return xs;
  }, [badge, meta]);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        // Optional theming parity with BadgeGallery
        ...(colors
          ? {
              // make pills sit on the same subtle surface
              '--badge-pill-bg': colors.bgSoft,
              '--badge-pill-fg': 'inherit',
            }
          : {}),
      }}
    >
      {pills.length ? (
        pills
      ) : (
        <span style={{ fontSize: 12, opacity: 0.8 }}>No stats available.</span>
      )}
    </div>
  );
}
